import React, { useState } from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import toast from 'react-hot-toast'
import { useAuth } from '@/src/hooks/useAuth'
import { mapRestoUserToAuthUser } from '@/src/@core/utils/map-resto-user-to-auth-user'
import { setDatadogUser } from '@/src/observability/datadog'
import { stopRoleImpersonation } from '@/src/services/http/roleImpersonation'

/** GYG brand: near-black + brand yellow (same as GygButton primary). */
const GYG_BLACK = '#171717'
const GYG_YELLOW = '#FFD204'
const GYG_YELLOW_HOVER = '#EDBF00'

/**
 * Persistent banner while session role impersonation is active.
 * Exit restores real role permissions via DELETE /resto365-user/me/impersonate-role.
 */
const RoleImpersonationBanner = () => {
  const { user, setUser } = useAuth()
  const [stopping, setStopping] = useState(false)

  const impersonation = user?.impersonation
  if (!impersonation?.active) {
    return null
  }

  const handleExit = async () => {
    setStopping(true)
    try {
      const me = await stopRoleImpersonation()
      setUser(mapRestoUserToAuthUser(me))
      setDatadogUser({ id: me.id, email: me.email, name: me.name })
      toast.success('Restored your real role')
    } catch {
      toast.error('Failed to stop role impersonation')
    } finally {
      setStopping(false)
    }
  }

  const label = impersonation.realRoleName
    ? `Viewing as ${impersonation.roleName} · signed in as ${impersonation.realRoleName}`
    : `Viewing as ${impersonation.roleName}`

  return (
    <Box
      role='status'
      aria-live='polite'
      sx={{
        position: 'relative',
        zIndex: theme => theme.zIndex.appBar + 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        px: { xs: 1.5, sm: 2 },
        py: 1,
        minHeight: 44,
        bgcolor: GYG_BLACK,
        color: GYG_YELLOW
      }}
    >
      <Typography
        variant='body2'
        title={label}
        sx={{
          minWidth: 0,
          flex: '1 1 auto',
          maxWidth: { xs: 'calc(100% - 88px)', sm: 560 },
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'center',
          fontWeight: 600,
          letterSpacing: '0.01em',
          color: GYG_YELLOW,
          lineHeight: 1.4
        }}
      >
        Viewing as <strong>{impersonation.roleName}</strong>
        {impersonation.realRoleName ? (
          <>
            {' '}
            <Box component='span' sx={{ fontWeight: 400, color: '#FFFFFF', opacity: 0.92 }}>
              (real role: {impersonation.realRoleName})
            </Box>
          </>
        ) : null}
      </Typography>

      <Button
        size='small'
        variant='contained'
        disableElevation
        onClick={() => void handleExit()}
        disabled={stopping}
        startIcon={stopping ? <CircularProgress size={14} sx={{ color: GYG_BLACK }} /> : undefined}
        sx={{
          flexShrink: 0,
          minWidth: 72,
          px: 1.75,
          py: 0.35,
          fontWeight: 700,
          textTransform: 'none',
          borderRadius: '24px',
          bgcolor: GYG_YELLOW,
          color: GYG_BLACK,
          border: `1px solid ${GYG_YELLOW}`,
          '&:hover': {
            bgcolor: GYG_YELLOW_HOVER,
            color: GYG_BLACK
          },
          '&.Mui-disabled': {
            bgcolor: '#EDBF00',
            color: GYG_BLACK,
            opacity: 0.7
          }
        }}
      >
        Exit
      </Button>
    </Box>
  )
}

export default RoleImpersonationBanner
